import React, { useEffect } from 'react';
import { useRouterParam } from '../../model/history';
import { useRemoteData } from '../../model/data';
import { getPicklist, deletePicklist } from '../../model/picklist';
import { useMeta, getPageTitle } from '../../model/meta';
import { picklistsPath } from '../../routes';
import { LoadingPage } from '../../components/LoadingPage/LoadingPage';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { t } from '../../locales/translate';
import { PermissionButton } from '../../components/Button/PermissionButton';
import { useConfirmation } from '../../components/ConfirmationPrompt/ConfirmationPrompt';

export function PicklistDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [picklist, loading, error] = useRemoteData(
    () => getPicklist(id),
    (db) => db.table('picklists').find(id),
    [id]
  );

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.picklist._name),
    message: t.general.confirmDelete(t.model.picklist._name),
    action: async () => {
      await deletePicklist({ picklistId: id });
    },
  });

  useMeta({
    title: getPageTitle(loading, picklist ? picklist.reference || picklist.id : '', picklist),
    breadcrumbs: [
      { title: t.model.picklist._plural, url: picklistsPath() },
      {
        title: getPageTitle(loading, picklist ? picklist.reference || picklist.id : '', picklist),
      },
    ],
  });

  useEffect(() => {
    if (!picklist || picklist.pdfUrl) {
      return;
    }
    let interval: ReturnType<typeof setTimeout>;
    async function update(): Promise<void> {
      const updated = await getPicklist(id);
      if (!updated.pdfUrl) {
        interval = setTimeout(update, 3000);
      }
    }
    interval = setTimeout(update, 3000);
    return () => clearTimeout(interval);
  }, [picklist && picklist.id]);

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.picklist._name)} />;
  } else if (error || !picklist) {
    return <Container>{t.errors.itemNotFound(t.model.picklist._name)}.</Container>;
  }

  const deleteButton = picklist && (
    <PermissionButton name="trash" model={picklist} permission="destroy" onClick={confirm} />
  );

  return (
    <Container>
      <Card title={picklist.reference} actions={deleteButton}>
        {(picklist.containsPicklist || picklist.containsDeliveries) && (
          <>
            {!picklist.pdfUrl && <LoadingPage title={t.actions.loading('PDF')} />}
            {picklist.pdfUrl && <iframe src={picklist.pdfUrl} width="100%" height="400" frameBorder="0" />}
          </>
        )}
        {picklist.containsTicketlist && (
          <>
            {!picklist.ticketlistPdfUrl && <LoadingPage title={t.actions.loading('PDF')} />}
            {picklist.ticketlistPdfUrl && (
              <iframe src={picklist.ticketlistPdfUrl} width="100%" height="400" frameBorder="0" />
            )}
          </>
        )}
      </Card>
    </Container>
  );
}
